/* eslint-disable react/jsx-props-no-spreading */
import { Suspense, useEffect, useRef, useState, useMemo, useCallback } from 'react';

import { useHistory } from 'react-router';

import { isAuthenticated } from 'actions/user';
import CustomLink from 'components/CustomLink/CustomLink';
import Render from 'components/render';
import { SIGNAL_TRADING } from 'constants/constants';
import { SIGNAL_TRADING_TUTORIAL_BLOG } from 'constants/urls';
import { getAllMarketsCards } from 'helpers/externalAPIHandlers';
import { noop } from 'helpers/utils';
import useTheme from 'hooks/useTheme';
import { appliedOffersSelector } from 'selectors/offersSelector';
import { userSelector } from 'selectors/otherSelectors';
import { useAppSelector } from 'storeHooks';
import AngleLeft from 'styles/assets/icons/AngleLeft';
import AngleRightIcon from 'styles/assets/icons/AngleRightIcon';
import Box from 'UIKit/Box';
import IconButton from 'UIKit/IconButton';

import { LIVE_NOW_TAG_ELIGIBLE_CARDS } from '../CardsArea.constants';
import { CardsRootContext } from '../CardsRootContext';
import { CUSTOM_CARDS_MAP, CustomCardRender } from '../CustomCardRender';
import { desktopFilters, trackCardClicks } from '../helpers';
import { LiveStatus } from '../LiveStatus';
import CombinedTopCoinsOverviewCards from '../MarketsOverviewCards/CombinedTopCoinsOverviewCards';
import { MarketCardValueTypes, TDesktopCard } from '../types';
import styles from './style.module.scss';
import TopSectionSkeletonLoader from './TopSectionSkeletonLoader';

const MINIMUM_BANNERS_TO_SCROLL = 2;
const BANNERS_WIDTH = 310;

const BannerImageWithLiveStatus = ({ type, imageURL, analyticsTag, index }) => {
  return (<>
    <Render when={LIVE_NOW_TAG_ELIGIBLE_CARDS.includes(type as MarketCardValueTypes)}>
      <LiveStatus offerType={type as MarketCardValueTypes} />
    </Render>
    <img
      className={styles.banner}
      src={imageURL}
      alt={analyticsTag}
      loading={index > 4 ? 'lazy' : 'eager'}
      data-palette="BannerImageWithLiveStatus" />
  </>);
};

const MarketingBannersCard = ({ card, idx }) => {
  const history = useHistory();

  const handleCardClick = (redirectURL, analyticsTag) => {
    trackCardClicks(analyticsTag);
    history.push(redirectURL);
  };

  const {
    type,
    analyticsTag,
    redirectURL,
    imageURL,
    openInNewTab,
    useLinkTag = false,
  } = card || {};

  if (type in CUSTOM_CARDS_MAP) {
    return <CustomCardRender key={`${type}`} card={card} className={styles.card} />;
  }

  if (!('redirectURL' in card || 'imageURL' in card || 'openInNewTab' in card))
    return null;

  if (useLinkTag) {
    return (
      <Box
        className={`${styles.card} ${styles.nonCustomCard}`}
        data-testid={`desktop-markets-card-${type}`}
        onClick={() => handleCardClick(redirectURL, analyticsTag)}
        key={`${imageURL}-${type}`}
      >
        <BannerImageWithLiveStatus
          type={type}
          imageURL={imageURL}
          analyticsTag={analyticsTag}
          index={idx}
        />
      </Box>
    );
  }

  return (
    // eslint-disable-next-line react/jsx-no-target-blank
    (<CustomLink
      data-testid={`desktop-markets-card-${type}`}
      key={`${imageURL}-${type}`}
      className={`${styles.card} ${styles.nonCustomCard}`}
      target={openInNewTab ? '_blank' : '_self'}
      rel={openInNewTab ? 'noopener noreferrer' : ''}
      href={redirectURL}
      onClick={() => trackCardClicks(analyticsTag)}
    >
      <BannerImageWithLiveStatus
        type={type}
        imageURL={imageURL}
        analyticsTag={analyticsTag}
        index={idx}
      />
    </CustomLink>)
  );
};

const TopSectionCards = () => {
  const [loading, setLoading] = useState(true);
  const user = useAppSelector(userSelector);
  const isLoggedIn = isAuthenticated(user);
  const appliedOffers = useAppSelector(appliedOffersSelector);
  const { activeTheme } = useTheme();
  const cardsRef = useRef(null);
  const [cards, setCards] = useState<TDesktopCard[]>([]);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [cardsToHide, setCardsToHide] = useState<Set<string>>(() => new Set());

  const onRequestCardToHide = useCallback(
    (cardType: string) => {
      if (cardsToHide.has(cardType)) return;
      setCardsToHide(prevHiddenCards => {
        const updatedHiddenCards = new Set(prevHiddenCards);
        updatedHiddenCards.add(cardType);
        return updatedHiddenCards;
      });
    },
    [cardsToHide]
  );

  const onRequestCardToShow = useCallback(
    (cardType: string) => {
      if (!cardsToHide.has(cardType)) return;
      setCardsToHide(prevHiddenCards => {
        const updatedHiddenCards = new Set(prevHiddenCards);
        updatedHiddenCards.delete(cardType);
        return updatedHiddenCards;
      });
    },
    [cardsToHide]
  );

  const contextValue = useMemo(() => {
    return {
      onRequestCardToHide,
      onRequestCardToShow,
    };
  }, [onRequestCardToHide, onRequestCardToShow]);

  const preventHorizontalScroll = e => {
    e.preventDefault();
  };

  useEffect(() => {
    if (cardsRef?.current) {
      cardsRef?.current?.addEventListener('wheel', preventHorizontalScroll, {
        passive: false,
      });

      cardsRef?.current?.addEventListener('touchmove', preventHorizontalScroll, {
        passive: false,
      });
    }

    return () => {
      if (cardsRef?.current) {
        cardsRef?.current?.removeEventListener('wheel', preventHorizontalScroll);
        cardsRef?.current?.removeEventListener('touchmove', preventHorizontalScroll);
      }
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    getAllMarketsCards()
      .then(result => {
        const { desktop = [] } = result;
        setCards(desktop);
      })
      // eslint-disable-next-line no-console
      .catch(e => console.log(`Error while loading all markets card`, e))
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const filteredCards = cards.reduce((filtered, card) => {
    if (cardsToHide.has(card.type)) {
      return filtered;
    }
    if (!desktopFilters({ card, isLoggedIn, user, appliedOffers, activeTheme })) {
      return filtered;
    }

    const processedCard =
      card.type === SIGNAL_TRADING
        ? {
            ...card,
            openInNewTab: !isLoggedIn,
            ...(!isLoggedIn && { redirectURL: SIGNAL_TRADING_TUTORIAL_BLOG }),
          }
        : card;

    filtered.push(processedCard);
    return filtered;
  }, [] as TDesktopCard[]);

  const onClickPrev = () => {
    if (!scrollPosition) return;
    setScrollPosition(scrollPosition - 1);
  };

  const onClickNext = () => {
    if (scrollPosition >= filteredCards.length) return;
    setScrollPosition(scrollPosition + 1);
  };

  useEffect(() => {
    if (cardsRef?.current) {
      const newScrollPos = scrollPosition * BANNERS_WIDTH;
      cardsRef.current?.scrollTo(newScrollPos, 0);
    }
  }, [scrollPosition]);

  const isDataLoaded = Boolean(filteredCards?.length > 0);
  const showSliderButtons =
    isDataLoaded && filteredCards.length > MINIMUM_BANNERS_TO_SCROLL;

  return (
    (<CardsRootContext.Provider value={contextValue} data-palette="TopSectionCards">
      <Box className={styles.container}>
        <Box className={styles.sliderSection}>
          <Box className={styles.prevButton} renderWhen={showSliderButtons}>
            <IconButton
              onClick={onClickPrev}
              data-testid="desktop-cards-right-icon"
              onKeyDown={noop}
              icon={<AngleLeft className={styles.arrow} />}
              disabled={!scrollPosition}
            />
          </Box>
          <Box className={styles.nextButton} renderWhen={showSliderButtons}>
            <IconButton
              onClick={onClickNext}
              data-testid="desktop-cards-right-icon"
              onKeyDown={noop}
              icon={<AngleRightIcon className={styles.arrow} />}
              disabled={
                scrollPosition === filteredCards.length - MINIMUM_BANNERS_TO_SCROLL
              }
            />
          </Box>
          <Box className={styles.cardsWrapper} ref={cardsRef}>
            <Suspense fallback={<TopSectionSkeletonLoader count={4} />}>
              <Render
                when={isDataLoaded || loading}
                else={<TopSectionSkeletonLoader count={4} />}
              >
                <Box className={styles.cards}>
                  <CombinedTopCoinsOverviewCards />
                  {/* <MarketTopCoinsOverviewCard /> */}
                  {filteredCards.map((card, idx) => (
                    <MarketingBannersCard card={card} idx={idx} />
                  ))}
                </Box>
              </Render>
            </Suspense>
          </Box>
        </Box>
      </Box>
    </CardsRootContext.Provider>)
  );
};

TopSectionCards.displayName = 'TopSectionCards';

export default TopSectionCards;
